/**
 * The words the operator reads on the content page: the two approval panels
 * (US-103), and since US-114 the stage bar, the list's status lines, the
 * next-step card and the production, review, versions and share panels.
 *
 * The operator is not assumed to know how the pipeline works, so nothing here
 * names a step, a file, a state or a status code: "Scenes", not storyboard or
 * sketch; "Narration", not voiceover or script step; "Text on screen", not
 * text blocks; "Length", not duration. Every label, notice and button text of
 * those panels comes from this file, so the vocabulary is
 * decided in one place and a later wording change touches nothing else.
 */
import {
  ApiError,
  type CampaignColor,
  type ContentFailureStage,
  type RetryAction,
  type ShotFraming,
  type SketchPlacement,
  type VisualKind,
} from './api.ts';

/* ------------------------------------------------------------------ lengths */

/** "1 second", "3 seconds", "1.5 seconds": a scene is timed to the tenth. */
function secondsPhrase(value: number): string {
  return `${String(value)} ${value === 1 ? 'second' : 'seconds'}`;
}

/**
 * A length in words: "3 seconds", "1 minute 5 seconds", "2 minutes" — never
 * `3s` or `65s`, which read as codes to someone who has not made a video.
 */
export function length(value: number): string {
  const tenths = Math.round(value * 10) / 10;
  if (tenths < 60) return secondsPhrase(tenths);
  const minutes = Math.floor(tenths / 60);
  const rest = Math.round((tenths - minutes * 60) * 10) / 10;
  const minutePhrase = `${String(minutes)} ${minutes === 1 ? 'minute' : 'minutes'}`;
  return rest === 0 ? minutePhrase : `${minutePhrase} ${secondsPhrase(rest)}`;
}

/** "1 scene", "4 scenes". */
export function sceneCount(count: number): string {
  return `${String(count)} ${count === 1 ? 'scene' : 'scenes'}`;
}

/* ------------------------------------------------------------------- shared */

export const WAITING_FOR_YOU = 'Waiting for you';
export const APPROVED = 'Approved';
export const NOT_APPROVED = 'Not approved yet';
export const WORKING = 'Working…';
export const SAVE = 'Save';
export const CANCEL = 'Cancel';
export const TRY_AGAIN = 'Try again';
export const SHOW_DETAILS = 'Show details';

/**
 * A refusal in a sentence. The server's own explanation when it sent one;
 * otherwise a plain sentence rather than the code and HTTP status the error
 * carries.
 */
export function plainError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.detail ?? 'That did not work. Try again in a moment.';
  }
  return 'That did not work. Try again in a moment.';
}

/* ---------------------------------------------------------------- narration */

export const NARRATION = 'Narration';
export const NARRATION_BADGE_NOT_APPROVED = NOT_APPROVED;
export const APPROVE_NARRATION = 'Approve narration';
export const SAVE_NARRATION = SAVE;
export const NARRATION_WORDS = 'Words';
export const NARRATION_LENGTH = 'Length';
export const NARRATION_APPROVED_AT = 'Approved';
export const NARRATION_FIELD = 'The narration, as it will be spoken';

/** The panel's one-line summary: "84 words, about 34 seconds when spoken". */
export function narrationSummary(words: number, seconds: number): string {
  return `${String(words)} ${words === 1 ? 'word' : 'words'}, about ${length(seconds)} when spoken`;
}

/** The value of the Length fact: "About 34 seconds". */
export function narrationLength(seconds: number): string {
  return `About ${length(seconds)}`;
}

export const NARRATION_GATE_NOTICE =
  'Check the narration before it is spoken. Change any line you like first.';
export const NARRATION_CHANGED_NOTICE =
  'Your change to the scenes also changed the narration. Approve it to go back to the scenes.';
export const NARRATION_AT_GATE =
  'The video is paused here. Nothing has been spoken yet, so a change now costs nothing.';
export const NARRATION_AFTER_GATE =
  'These are the words the video speaks. A change here is used the next time the voice is made.';
export const NARRATION_HINT =
  'Put the narration of each scene on its own line. A blank line makes a long pause.';

/** The plan's `## Script changes` under the narration (US-016). */
export const SCRIPT_CHANGES_TITLE = 'Changed from the ideas document';
export const SCRIPT_CHANGES_HINT =
  'The planner fixed these lines of the ideas document so the story follows and every line can be heard. What you typed in the brief is never changed.';
export const SCRIPT_CHANGES_DROPPED = '(line dropped)';

/** The plan's `## Decisions made` (one-shot ads US-021): what an autopilot planner chose itself. */
export const DECISIONS_TITLE = 'Decided on autopilot';
export const DECISIONS_HINT =
  'Nobody was asked: Claude took these itself — from the house rules, then the product profile and the ad kit, then contents you approved, then the tone, and only then its own recommendation.';
export const DECISIONS_NONE = 'Claude has not recorded a decision yet.';
/** The plan's `## Claims` ledger (the ad bible US-014): every claim the ad makes and where it comes from. */
export const CLAIMS_TITLE = 'Claims ledger';
export const CLAIMS_HINT =
  'Every figure, date, customer and guarantee the ad states, with its source. A dramatised figure is scene detail, never the proof.';
export const CLAIMS_REPAIRS =
  'Not backed yet — the planner repairs these itself; they do not stop Mark ready.';
/** The reply box of an autopilot planning conversation: a change, never an answer. */
export const PLANNING_CHANGE = 'Change the plan';
export const PLANNING_CHANGE_HINT =
  'Claude applies it as a change and decides what it leaves open, without asking back. Ctrl+Enter (⌘+Enter on a Mac) sends it.';

export const NARRATION_SAVED = 'Saved. The video speaks these words.';
export const NARRATION_APPROVED = 'Narration approved.';
export const NARRATION_APPROVED_CONTINUES = 'Narration approved. The video is being made.';
export const NARRATION_APPROVED_BACK_TO_SCENES = 'Narration approved. Back to the scenes.';

/* -------------------------------------------------------------- the gate strip */

/** The approvals and what follows them, as the strip at the top of both gate panels (US-105). */
export const GATE_STEP_NARRATION = 'Narration';
export const GATE_STEP_SCENES = 'Scenes';
export const GATE_STEP_VIDEO = 'Video is made';
export const GATE_STEPS_LABEL = 'Where you are';
export const SCENES_APPROVAL_STARTS =
  'When you approve, the video is made from these scenes. This is where paid generation starts.';
export const SCENES_COMING = 'Getting the scenes ready…';

/* ------------------------------------------------------------------- scenes */

export const SCENES = 'Scenes';
export const APPROVE_SCENES = 'Approve scenes';
export const CHANGE_WITH_REQUEST = 'Change with a request';
export const EDIT_THIS_SCENE = 'Edit this scene';
export const SCENES_GATE_NOTICE =
  'Check the scenes before the video is made. Change anything you like first.';
export const SCENES_BUSY = 'Working on the scenes…';
export const SCENES_HAVE_PROBLEMS = 'Fix the problems marked above to continue.';
export const SCENES_LAST_CHANGE = 'Last change';
export const UNDO_LAST_CHANGE = 'Undo last change';
/** Under "Last change" when the change was not described, such as a hand edit (US-112). */
export const SCENES_CHANGED = 'The scenes were changed.';
export const SCENES_UNDONE = 'Your last change was undone.';
export const SCENES_REQUEST_LABEL = 'What should change?';
export const SCENES_REQUEST_PLACEHOLDER = 'Describe what you want to change, in your own words';
export const SCENES_REQUEST_HINT = 'You can ask for anything: other scenes, other words, a different order.';
/** The requests shown as buttons under the request box (US-106); a press fills the box, never sends. */
export const SCENES_REQUEST_EXAMPLES = [
  'Make the video shorter',
  'Add a scene that shows the price',
  'Remove the last scene',
  'Use less text on screen',
  'Make the opening more exciting',
] as const;
export const SCENES_REQUEST_EXAMPLES_LABEL = 'Examples';
export const CHANGE_THIS_SCENE = 'Change this scene';
export const SCENES_APPROVED = 'Scenes approved. The video is being made.';

/* ------------------------------------------------------- the waiting (US-110) */

export const SCENES_FITTING = 'Fitting the scenes to your narration.';
/** The request being worked on, in quotes: `Working on your request: “Make it shorter”`. */
export function workingOnRequest(request: string): string {
  return `Working on your request: “${request}”`;
}
export const USUALLY_A_MINUTE = 'This usually takes about a minute.';
/** How long a run may take before the panel says it is taking longer than usual. */
export const LONG_WAIT_SECONDS = 180;
export const TAKING_LONGER =
  'This is taking longer than usual. You can keep waiting or leave this page; nothing is lost.';
/** The time passed since the run started, as `m:ss`. */
export function timePassed(clock: string): string {
  return `${clock} so far`;
}
export const SCENES_FAILED = 'That did not work. Your scenes are unchanged.';
export const CHANGE_MY_REQUEST = 'Change my request';

/* ------------------------------------------ keyboard and screen reader (US-111) */

/** Read out when a fit or a request has finished and the scenes are redrawn. */
export const SCENES_UPDATED = 'Done. The scenes are updated.';
/** Read out after a hand edit went through: "Scene 2 is saved." */
export function sceneSaved(number: number): string {
  return `Scene ${String(number)} is saved.`;
}
/** Before a problem's sentence, for a screen reader (the red and the icon say it to the eye). */
export const PROBLEM_PREFIX = 'Problem: ';

/* ----------------------------------------- confirm and protect edits (US-109) */

export const APPROVE_SCENES_TITLE = 'Make the video from these scenes?';
export const APPROVE_SCENES_YES = 'Yes, make the video';
export const GO_BACK = 'Go back';
export const APPROVE_SCENES_COST =
  'Paid generation starts now. You can still ask for changes when the video is ready for review.';
export const DISCARD = 'Discard';
export const SAVING = 'Saving…';
export const SCENE_UNSAVED = 'You changed this scene but did not save it yet.';

/** "The video is made from 4 scenes, 22 seconds in all." */
export function approveScenesFacts(count: number, seconds: number): string {
  return `The video is made from ${sceneCount(count)}, ${length(seconds)} in all.`;
}

/** "Save your changes to scene 2 first?" */
export function saveSceneFirst(sceneNumber: number): string {
  return `Save your changes to scene ${String(sceneNumber)} first?`;
}

/** The panel's header: "4 scenes, 22 seconds". */
export function scenesSummary(count: number, seconds: number): string {
  return `${sceneCount(count)}, ${length(seconds)}`;
}

/* ------------------------------------------- what the last change did (US-107) */

/** The badge of a scene the last change added or changed. */
export const SCENE_CHANGE = { new: 'New', changed: 'Changed' } as const;

/** "Was 30 seconds, now 24 seconds": the header when the last change moved the total. */
export function lengthChange(before: number, now: number): string {
  return `Was ${length(before)}, now ${length(now)}`;
}

/** "Removed: scene 4 (Pricing)", or "Removed: scene 3 (Proof) and scene 4 (Pricing)". */
export function removedScenes(removed: readonly { number: number; name: string }[]): string {
  const named = removed.map((scene) => `scene ${String(scene.number)} (${scene.name})`);
  const list =
    named.length <= 1 ? named.join('') : `${named.slice(0, -1).join(', ')} and ${named.at(-1) ?? ''}`;
  return `Removed: ${list}`;
}

/* -------------------------------------------------------------- one scene */

export const SCENE_SHOWN = 'What you see';
export const SCENE_SHOWN_UNKNOWN = 'Not decided yet';
export const TEXT_ON_SCREEN = 'Text on screen';
export const NO_TEXT_ON_SCREEN = 'No text on screen';
export const ADD_TEXT = 'Add text';
export const SCENE_NARRATION = NARRATION;
export const NO_NARRATION = 'No narration';
export const NARRATION_BY_REQUEST = 'To change the narration, use a request below the scenes.';
export const SCENE_ALSO = 'Also in this scene';
export const SCENE_LENGTH = 'Length in seconds';
export const SCENE_LENGTH_REFUSED = 'The length is a number of seconds greater than 0.';

/** "Scene 2". */
export function sceneLabel(number: number): string {
  return `Scene ${String(number)}`;
}

/** The label of the name field of a scene being edited: "Name of scene 2". */
export function sceneNameLabel(number: number): string {
  return `Name of scene ${String(number)}`;
}

/** What the Edit button of scene 2 is to a screen reader. */
export function editSceneLabel(number: number): string {
  return `Edit scene ${String(number)}`;
}

/** What the "Change this scene" button of scene 2 is to a screen reader. */
export function changeSceneLabel(number: number): string {
  return `Change scene ${String(number)} with a request`;
}

/** How "Change this scene" starts a request: `In scene 2 ("What it is"): `. */
export function sceneRequestStart(number: number, name: string): string {
  return `In scene ${String(number)} ("${name}"): `;
}

/** "Text on screen 1", for the inputs of a scene being edited. */
export function textOnScreenLabel(index: number): string {
  return `Text on screen ${String(index)}`;
}

/** "Remove text on screen 1". */
export function removeTextLabel(index: number): string {
  return `Remove text on screen ${String(index)}`;
}

/** Under the scene's name: "3 seconds, starts at 6 seconds". */
export function sceneTiming(seconds: number, startsAt: number): string {
  return startsAt === 0
    ? `${length(seconds)}, at the start`
    : `${length(seconds)}, starts at ${length(startsAt)}`;
}

/** The kinds of visual a scene can have, in the operator's words. */
export const VISUAL_KIND: Record<VisualKind, string> = {
  capture: 'Screen recording',
  image: 'Picture',
  animation: 'Animation',
  clip: 'Generated video',
  talk: 'Person talking',
  graphic: 'Text and graphics',
};

/** How a scene’s `**Shot:**` line frames it, as a small label next to its kind of visual (US-009). */
export const SHOT_LABEL_TITLE = 'How the shot is framed';
export const SHOT_FRAMING: Record<ShotFraming, string> = {
  establishing: 'Establishing',
  wide: 'Wide',
  'two-shot': 'Two-shot',
  'over-the-shoulder': 'Over the shoulder',
  medium: 'Medium',
  'close-up': 'Close-up',
  reaction: 'Reaction',
  insert: 'Insert',
  'point-of-view': 'Point of view',
  backdrop: 'Backdrop',
};

/**
 * What is placed in a scene, as a sentence: "A person talks from 1.5 seconds
 * into this scene". The step and the beat code behind it are not the
 * operator's business.
 */
export function placementSentence(placement: SketchPlacement): string {
  const what = placement.type === 'talk' ? 'A person talks' : 'An animation plays';
  return placement.atSeconds === 0
    ? `${what} from the start of this scene`
    : `${what} from ${length(placement.atSeconds)} into this scene`;
}

/* ---------------------------------------------------------------- attention */

/*
 * The line a content carries in the list and under a stage of the progress
 * bar: lower case, because it follows the content's name (US-114).
 */
export const ATTENTION_SCENES = 'scenes waiting for your approval';
export const ATTENTION_NARRATION = 'narration waiting for approval';
export const ATTENTION_PLAN = 'plan is written, confirm it to continue';
export const ATTENTION_MISSED_START = 'missed its scheduled start — start it now';
export const ATTENTION_ON_HOLD = 'paused until Claude can work again';
export const ATTENTION_SETUP_FAILED = 'Setup failed';
export const ATTENTION_PLANNER_ASKED = 'Claude asked you something';
export const ATTENTION_PLANNING_STOPPED = 'Planning stopped';
export const ATTENTION_START_PLANNING = 'Start planning';
export const ATTENTION_START_PRODUCTION = 'Start production';

/** "paused until Claude can work again · resumes in 12 min". */
export function attentionOnHold(resumes: string): string {
  return `${ATTENTION_ON_HOLD} · resumes ${resumes}`;
}

/** "version 2 waiting for your review". */
export function attentionReview(version: number): string {
  return `version ${String(version)} waiting for your review`;
}

/** "1 post is due", "3 posts are due". */
export function attentionPostsDue(due: number): string {
  return due === 1 ? '1 post is due' : `${String(due)} posts are due`;
}

/** "stopped: the plan could not be read". */
export function attentionFailed(stage: ContentFailureStage | null): string {
  return `stopped: ${FAILURE_SHORT[stage ?? 'unknown']}`;
}

/* ------------------------------------------------------------ the stage bar */

export const STAGE_PLAN = 'Plan';
export const STAGE_READY = 'Ready';
export const STAGE_MAKING = 'Making the video';
export const STAGE_REVIEW = 'Your review';
export const STAGE_DONE = 'Done';

/* ------------------------------------------------------------------ failure */

/*
 * What went wrong, by the stage the server recorded, in words that name no
 * container, probe, file or agent. The server's own reading, and the error it
 * stored, wait behind "Show details".
 */
const FAILURE_SHORT: Record<ContentFailureStage | 'unknown', string> = {
  setup: 'the project files could not be fetched',
  agent: 'the work stopped before it was finished',
  plan: 'the plan could not be read',
  probe: 'a part of the video kept coming out wrong',
  container_lost: 'the work was interrupted',
  upload: 'a finished part could not be saved',
  budget: 'the spending limit was reached',
  review: 'the check of the video did not finish',
  unknown: 'something went wrong',
};

const FAILURE_SENTENCE: Record<ContentFailureStage | 'unknown', string> = {
  setup: 'The project files could not be fetched, so the work could not start.',
  agent: 'The work on the video stopped before it was finished.',
  plan: 'The plan could not be read any more, so the work stopped.',
  probe: 'A part of the video came out wrong three times in a row, so the work stopped.',
  container_lost: 'The work was interrupted before the video was finished. Nothing made so far is lost.',
  upload: 'A finished part of the video could not be saved.',
  budget: 'The video reached its spending limit, so the work stopped.',
  review: 'The check of the finished video did not finish.',
  unknown: 'Something went wrong while making the video.',
};

/** The one sentence a failed content opens with. */
export function failureSentence(stage: ContentFailureStage | null): string {
  return FAILURE_SENTENCE[stage ?? 'unknown'];
}

export const SETUP_NOT_RUN = 'The project files have not been fetched yet.';
export const SETUP_FAILED = FAILURE_SENTENCE.setup;
export const RETRY_SETUP = 'Fetch the project files again';
export const NO_ERROR_RECORDED = 'Nothing was recorded about why.';

/** The one button of a failed content: fetch the files again, or pick the work up where it stopped. */
export function retryLabel(action: RetryAction | null): string {
  return action === 'setup' ? RETRY_SETUP : TRY_AGAIN;
}

/* ----------------------------------------------------------------- problems */

/*
 * A problem is shown as the server's plain sentence with a button that sends
 * its ready-made request; the technical message waits behind "Show details".
 */
export const FIX_THIS = 'Fix this for me';
export const FIX_ALL = 'Fix all problems';

/** One request for every problem at once: the ready-made requests as a list. */
export function fixAllPrompt(fixPrompts: readonly string[]): string {
  return ['Fix all of these problems:', ...fixPrompts.map((prompt) => `- ${prompt}`)].join('\n');
}

/** The timeline strip above the scene cards (US-108). */
export const SCENE_STRIP_LABEL = 'The video as a row of scenes';

/** A strip segment's name: "Scene 2, What it is, 5 seconds", plus a problem when it has one. */
export function sceneSegmentLabel(
  scene: { readonly number: number; readonly name: string; readonly seconds: number },
  hasProblem: boolean,
): string {
  const label = `Scene ${String(scene.number)}, ${scene.name}, ${length(scene.seconds)}`;
  return hasProblem ? `${label}, has a problem` : label;
}

/* ----------------------------------------------------- the next-step card */

export const SHOW_ME_NOW = 'Show me now';
export const SHOW_ME_NOW_HINT = 'Stop improving the video and show me what there is now';
export const SHOW_ME_NOW_STOPPING = 'Already stopping; the video shows up as soon as the current part is finished.';

export const STOPPING = 'Stopping…';
export const BACK_TO_PLANNING = 'Back to planning';
export const BACK_TO_PLANNING_HINT = 'Change the plan before the video is made';
export const DOWNLOAD = 'Download';
export const DOWNLOAD_NOT_YET = 'The file is not ready to download yet; the player below shows what there is';
export const REQUEST_CHANGES = 'Request changes';
export const CANNOT_APPROVE_YET = 'This version cannot be approved yet.';

/* ------------------------------------------------------------ making the video */

export const PRODUCTION = 'Making the video';
export const PRODUCTION_STARTING = 'Starting to make the video…';
export const PRODUCTION_STEPS = 'Parts of the video';
export const PRODUCTION_FAILED_BADGE = 'stopped here';
export const SHOW_THE_LOG = 'Show the log';

/** "Part 3 of 8: Record the demo". */
export function productionAt(position: number, total: number, title: string): string {
  return `Part ${String(position)} of ${String(total)}: ${title}`;
}

/** "Try 2 of 3 · going for 1:05". */
export function productionAttempt(attempt: number, max: number, running: string | null): string {
  const tries = `Try ${String(attempt)} of ${String(max)}`;
  return running === null ? tries : `${tries} · going for ${running}`;
}

/** Under the list of a failed video: how far it got. */
export function productionStopped(done: number, total: number): string {
  return `Stopped after ${String(done)} of ${String(total)} parts. The card above says what went wrong; the part it stopped on is marked below.`;
}

/** The notice while Claude's usage limit pauses the work. */
export function productionOnHold(until: string | null): string {
  return `Paused until ${until ?? 'Claude can work again'}. Claude’s usage limit was reached; the work picks up where it stopped, and this pause costs nothing.`;
}

/* --------------------------------------------------------------- your review */

export const REVIEW = 'Your review';
export const DELIVERED = 'Done';
export const REVIEW_GOOD = 'Looks good';
export const REVIEW_NEEDS_CHANGES = 'Needs changes';
export const CHECK_VIDEO = 'Check the video';
export const CHECK_VIDEO_AGAIN = 'Check again';
export const CHECK_VIDEO_HINT = 'Check the video for problems';
export const CHECKING_VIDEO = 'Checking…';
export const REVIEW_CHANGES_LABEL = 'What should change?';
export const REVIEW_CHANGES_PLACEHOLDER = 'Put the button above the caption; make scene 3 shorter.';
export const REVIEW_NOT_CHECKED =
  'This version has not been checked yet. The check watches it as an ad: it scores the hook, how clear it is, the ask and how it moves, hears the sound back against your words, and lists what it finds here.';
export const REVIEW_NO_FINDINGS_STORED = 'There is nothing to show for this version.';
export const REVIEW_NO_FINDINGS = 'The check found no problems.';
export const REVIEW_CHECKED_CLEAN = 'Checked and fine';
export const REVIEW_HISTORY = 'Earlier changes';
export const REVIEW_VERSION = 'Version';
export const REVIEW_CHECKED_AT = 'Checked';
export const REVIEW_MODEL = 'Checked by';
export const REVIEW_MODEL_DEFAULT = 'Claude';
export const REVIEW_APPROVING =
  'Approving marks this video as done. Nothing is published anywhere; you post it yourself.';
export const REVIEW_DELIVERING = 'Marking the video as done…';
export const REVIEW_CHECKING = 'Checking the video: measuring how it moves, hearing it back, then watching it…';
export const REVIEW_REVISING = 'Changing the plan for the next version…';

/** The severities of a finding, as a group title. */
export const FINDING_GROUP = { block: 'Must fix', warning: 'Worth a look', note: 'Notes' } as const;

/** What each score is called on the page (synthesis S6). */
export const REVIEW_SCORE_LABEL = {
  hook: 'Hook',
  clarity: 'Clear to a stranger',
  brand: 'Brand by 5 seconds',
  proof: 'Proof',
  ask: 'The ask',
  sync: 'Picture on the voice',
  motion: 'Smooth motion',
  look: 'Bold and on-brand',
  finish: 'Finish',
} as const;

/** What owns a problem, in the page's words. */
export const REVIEW_LAYER_LABEL = {
  words: 'your words',
  storyboard: 'the scenes',
  compose: 'the picture',
  audio: 'the sound',
  capture: 'the footage',
  render: 'the file',
} as const;

export const REVIEW_EVERYWHERE = 'everywhere it happens';
export const REVIEW_SUGGESTIONS = 'About your words';
export const REVIEW_SUGGESTIONS_HINT =
  'You approved these words, so nothing changes them by itself. Change them with a request if you agree.';
export const REVIEW_MEASURED = 'What was measured before the check';

/** "Scores: 19 of 27". */
export function reviewScoresSummary(total: number, out: number): string {
  return `Scores: ${String(total)} of ${String(out)}`;
}

/** A finding's area as the page says it: a score's own name, or the question as written. */
export function findingArea(area: string): string {
  return (REVIEW_SCORE_LABEL as Record<string, string>)[area] ?? area;
}

/** "3 things the checks noticed". */
export function probeWarnings(count: number): string {
  return `${String(count)} ${count === 1 ? 'thing' : 'things'} the automatic checks noticed`;
}

/** A measurement that could not be made. */
export function notMeasured(reason: string): string {
  return `Not measured: ${reason}`;
}

/** "Approve version 2". */
export function approveVersion(version: number): string {
  return `Approve version ${String(version)}`;
}

/** "Request changes and make version 3". */
export function requestChangesAndMake(version: number): string {
  return `Request changes and make version ${String(version)}`;
}

/** Under the request box: what sending it does. */
export function reviewChangesHint(version: number): string {
  return `Your request and the problems listed below are used to make version ${String(version + 1)}. What is already made is kept, and version ${String(version)} stays exactly as it is.`;
}

/** "The review found 2 problems. Approve it as it is, or ask for changes." */
export function reviewBlocking(count: number): string {
  return `The review found ${String(count)} ${count === 1 ? 'problem' : 'problems'}. Approve it as it is, or ask for changes.`;
}

/** "Checked 12 frames". */
export function framesChecked(count: number): string {
  return `${String(count)} ${count === 1 ? 'picture' : 'pictures'} checked`;
}

/** "Checked and fine (4)". */
export function checkedClean(count: number): string {
  return `${REVIEW_CHECKED_CLEAN} (${String(count)})`;
}

/** Under the review when the video is done. */
export function deliveredOn(when: string): string {
  return `Approved ${when}. Everything made for it is kept here.`;
}

/** An earlier request: what came of it. */
export function revisionMade(version: number | null, state: 'running' | 'failed' | null = null): string {
  if (state === 'running') return 'the plan is being revised';
  if (state === 'failed') return 'not made';
  return version === null ? 'the next version is still being made' : `made version ${String(version)}`;
}

/** While the revision pass edits the plan, after **Request changes** answered. */
export function revisionRunning(version: number): string {
  return `Planning version ${String(version)} from your changes. This takes a minute or two; production starts by itself.`;
}

/** A revision that could not amend the plan: the plan is as it was. */
export function revisionFailed(version: number, why: string): string {
  return `Version ${String(version)} could not be made from your changes: ${why}`;
}

/* ---------------------------------------------------------- automatic rounds */

export const ROUNDS = 'Improving the video by itself';
export const ROUNDS_HISTORY = 'What each round did';
export const ROUNDS_WORKING_ON = 'Being worked on';
export const ROUNDS_FIXED = 'Fixed';
export const ROUNDS_REMAINING = 'Still there';
export const ROUNDS_NEW = 'New';
export const ROUNDS_STARTING = 'Starting';
export const ROUNDS_STOPPED =
  'You stopped the automatic rounds, so no further one will start. Requesting changes yourself still makes the next version.';

/** "Round 1 of 3: <what it is doing>". */
export function roundAt(round: number, limit: number, phase: string): string {
  return `Round ${String(round)} of ${String(limit)}: ${phase}`;
}

/** "round 1 of 3". */
export function roundBadge(round: number, limit: number): string {
  return `round ${String(round)} of ${String(limit)}`;
}

/* ----------------------------------------------------------------- versions */

export const VERSIONS = 'Versions';
export const VERSION_CURRENT = 'latest';
export const CAPTIONS = 'Captions';
export const CAPTIONS_ON = 'Captions on';
export const CAPTIONS_OFF = 'Captions off';
export const CAPTIONS_HINT = 'Show or hide the captions in the player';
export const NO_CAPTIONS = 'This version has no captions.';
export const DOWNLOAD_MAIN = 'Video';
export const DOWNLOAD_IMAGE = 'Image';
export const DOWNLOAD_WAITING = 'Getting the download ready…';
export const DOWNLOAD_NOT_STORED = 'Not ready to download yet';
export const ZOOM = 'Zoom';
export const ZOOM_FIT = 'Fit';
export const VERSION_NO_FILE =
  'The file of this version is no longer available. It was made on another server, or its files were removed.';
export const VERSION_NOT_STORED = 'This version is not ready to play yet. Try again in a moment.';
export const VERSION_NOT_FETCHED =
  'This version could not be loaded. Check the storage connection in Settings.';

/** "Version 2". */
export function versionTitle(version: number): string {
  return `Version ${String(version)}`;
}

/** The badge of an older version: "replaced by version 3". */
export function versionReplaced(current: number): string {
  return `replaced by version ${String(current)}`;
}

/** "made 5 minutes ago". */
export function versionMade(since: string): string {
  return `made ${since}`;
}

/** The folded panel's summary: "version 3 is the latest, 3 versions". */
export function versionsSummary(current: number, count: number): string {
  return `version ${String(current)} is the latest${count > 1 ? `, ${String(count)} versions` : ''}`;
}

/* --------------------------------------------------------------- share copy */

export const SHARE_COPY = 'Posts to share';
export const COPY = 'Copy';
export const COPY_ALL = 'Copy all';
export const COPY_ALL_HINT = 'Copy every post at once';
export const SHARE_NOTHING_WRITTEN = 'nothing written';
export const SHOW_ALTERNATIVES = 'Show other versions of the posts';
export const HIDE_ALTERNATIVES = 'Hide other versions of the posts';
export const ALTERNATIVES_HINT = 'Other ways to word the same posts';
export const COPY_BLOCKED = 'The browser blocked copying; select the text instead.';

/** "Posts for 2 platforms". */
export function platformsWritten(written: number): string {
  return written === 0
    ? 'No post written yet'
    : `Posts for ${String(written)} ${written === 1 ? 'platform' : 'platforms'}`;
}

/** "2 platforms". */
export function platformsBadge(written: number): string {
  return `${String(written)} ${written === 1 ? 'platform' : 'platforms'}`;
}

/** "LinkedIn copied." */
export function copied(label: string): string {
  return `${label} copied. Paste it where you post.`;
}

/** "280 characters". */
export function characters(count: number): string {
  return `${String(count)} ${count === 1 ? 'character' : 'characters'}`;
}

/** The row of a platform nothing was written for. */
export function shareMissing(platform: string): string {
  return `No post was written for ${platform}. Request changes to get one, or write it yourself from the others.`;
}

/** An attribution line a licence asks for. */
export function creditLine(source: string, carried: boolean, note: string): string {
  return carried
    ? `is required by ${source} and the posts include it.`
    : `is required by ${source} and these posts do not include it yet. ${note}`;
}

export const DELIVERED_SENTENCE = 'Done. This video is approved.';

/** A size of the video that has no format name: "Other size 2". */
export function otherSize(index: number): string {
  return `Other size ${String(index)}`;
}

export const ALL_POSTS = 'All posts';

/* ------------------------------------------------ one part of the video, opened */

export const PART_ID = 'Step';
export const PART_TRIES = 'Tries';
export const PART_ORDER = 'Order';
export const PART_LAST_TRY = 'Last try took';
export const PART_CHECK = 'Files';
export const PART_FILE_PRESENT = 'present';

/** The badge of what the check of a part's files found. */
export function partCheck(ok: boolean, problems: number): string {
  if (ok) return 'look right';
  return problems === 0 ? 'have a problem' : `${String(problems)} ${problems === 1 ? 'problem' : 'problems'}`;
}

/** The badge's tooltip: when the files were checked and the first thing wrong. */
export function partCheckHint(when: string, ok: boolean): string {
  return ok
    ? `Checked ${when}: every file of this part is there and looks right.`
    : `Checked ${when}: something about the files of this part is not right.`;
}

export const PLAN_WRITTEN = 'The plan is written. Confirm it with Mark ready to continue.';
export const READY_TO_MAKE = 'Ready to make the video.';
export const MAKING = 'Making the video.';

/** "Making the video: part 3 of 8, Record the demo." */
export function makingAt(position: number, total: number, title: string | null): string {
  return `Making the video: part ${String(position)} of ${String(total)}${title === null ? '' : `, ${title}`}.`;
}

/** "Waiting for its turn (number 2 in line). Nothing is needed from you." */
export function waitingInLine(position: number): string {
  return `Waiting for its turn (number ${String(position)} in line). Nothing is needed from you.`;
}

/* The Advanced details section of the content page (US-115). */

export const ADVANCED_DETAILS = 'Advanced details';
export const ADVANCED_DETAILS_HINT =
  'The plan, its steps, what Claude and the clips cost, the video model, the library and the container.';

/** The one sentence under the estimated total. */
export function estimatedCostSentence(kind: 'video' | 'image'): string {
  return kind === 'video' ? 'Estimated cost of making this video' : 'Estimated cost of making these images';
}

/** "The plan has 2 problems to fix before it can run." — the notice outside the section. */
export function planProblemsNotice(problems: number): string {
  return `The plan has ${String(problems)} ${problems === 1 ? 'problem' : 'problems'} to fix before it can run.`;
}

export const SHOW_THE_PLAN = 'Show the plan';

/** The over-cap warning beside the estimated total; the full reasoning is behind Show details. */
export function overCapNotice(capUsd: number): string {
  return `This is more than the spend cap of $${capUsd.toFixed(2)}. It can still be made; raise the cap on this content to be sure nothing stops halfway.`;
}

/* ------------------------------------------------- how approval works (US-113) */

export const HOW_APPROVAL_WORKS = 'How approval works';
export const CLOSE = 'Close';
export const GOT_IT = 'Got it';
/** Above the scenes, until "Got it" is pressed in this browser: three sentences. */
export const SCENES_INTRO =
  'These are the scenes of your video, in the order they play. Change them with a request, or edit a scene yourself. When they look right, approve them and the video is made.';

/* ------------------------------------------------ where a content comes from */

export const LANGUAGE = 'Language';
export const ADDRESS_LABEL = 'How the ad addresses the viewer';
export const ADDRESS_CHOICES = [
  ['je', 'je — informal'],
  ['u', 'u — formal'],
  ['', 'not decided'],
] as const;
export const ADDRESS_HINT =
  'Dutch also needs one form of address throughout; leave it undecided and the planner asks.';
export const CONTENT_LANGUAGE_HINT =
  'What the script is written in and the voice speaks. Filled in from the product’s profile when it has one, otherwise from Settings → Defaults.';
export const PRIMARY_TAG = 'primary';
export const MAKE_PRIMARY = 'Make primary';

/* -------------------------------------------------------------------- ideas */

/*
 * The Ideas pages (US-122). A batch is a list of ideas worked out into
 * content proposals; the operator reads "ideas", "worked out", "to decide" —
 * never the pipeline's words for the line typed in, the state of a proposal,
 * or the process that writes them.
 */

export const IDEAS = 'Ideas';
export const IDEAS_SUBTITLE =
  'Lists of ideas Claude works out into proposals for content. You decide which ones get made.';
export const NEW_BATCH = 'New batch';
export const NO_BATCHES = 'No batches yet';
export const NO_BATCHES_BODY =
  'A batch is a list of ideas for one product: you type them, or Claude suggests them from a theme. Claude works each one out into a proposal — a title, a brief, formats and a tone — and you approve the ones worth making. An approved idea becomes a content and starts planning.';
export const IDEAS_READ_FAILED = 'Could not read the batches:';
export const BATCH_COLUMN = 'Batch';
export const BATCH_REPOSITORY = 'Repository';
export const BATCH_IDEAS = 'Ideas';
export const BATCH_PROGRESS = 'Progress';
export const BATCH_COST = 'Cost';
export const BATCH_CREATED = 'Created';
export const NO_REPOSITORY = 'none';
export const OPEN = 'Open';

/** "1 idea", "25 ideas". */
export function ideaCount(count: number): string {
  return `${String(count)} ${count === 1 ? 'idea' : 'ideas'}`;
}

/** Where a batch stands, in the badge beside its title. */
export const BATCH_STATUS = {
  queued: 'Waiting to start',
  working: WORKING,
  done: 'Done',
  failed: 'Could not be started',
} as const;

/** "12 of 25 worked". */
export function ideasWorked(worked: number, total: number): string {
  return `${String(worked)} of ${String(total)} worked`;
}

/** "3 to decide". */
export function ideasToDecide(count: number): string {
  return `${String(count)} to decide`;
}

export const IDEAS_ALL_DECIDED = 'done';

/** "3 ideas waiting for your decision", in the sidebar and on the Overview (US-125). */
export function ideasWaiting(count: number): string {
  return `${ideaCount(count)} waiting for your decision`;
}

export const IDEAS_WORKING = 'Working on your ideas';
export const ALL_BATCHES = 'All batches';
export const IDEAS_SUGGESTING = 'Suggesting ideas…';
export const NOTHING_WORKED_OUT = 'nothing worked out yet';

/** "3 could not be worked out". */
export function ideasNotWorked(count: number): string {
  return `${String(count)} could not be worked out`;
}

/** Dollars with two decimals, the way the content page shows Claude's cost. */
export function dollars(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

/* ---------------------------------------------------------- the batch page */

export const BATCH_BACK = 'Ideas';
export const BATCH_READ_FAILED = 'Could not read the batch:';
export const BATCH_PRIMARY = 'Product';
export const BATCH_THEME = 'Theme';
export const BATCH_NO_THEME = 'Your own ideas';
export const REVIEW_ONE_BY_ONE = 'Review one by one';
export const DELETE_BATCH = 'Delete batch';
export const DELETE_BATCH_WHILE_WORKING =
  'A batch cannot be deleted while Claude is working on it. Wait until it is done.';
export const DELETE_BATCH_TITLE = 'Delete this batch?';
export const DELETE_BATCH_BODY =
  'The batch and every idea in it are removed. Contents already made from its ideas stay where they are.';
export const DELETE_BATCH_YES = 'Delete batch';
export const DELETING = 'Deleting…';
export const BATCH_DELETED = 'The batch is deleted.';
export const BATCH_RETRIED = 'Trying again.';

/** "Working on idea 7 of 25". */
export function workingOnIdea(number: number, total: number): string {
  return `Working on idea ${String(number)} of ${String(total)}`;
}

/** "Paused until 14:30 — Claude's limit". */
export function pausedUntil(clock: string): string {
  return `Paused until ${clock} — Claude’s limit`;
}

/** "Done — 12 to decide", "Done — nothing left to decide". */
export function batchDone(toDecide: number): string {
  return toDecide === 0 ? 'Done — nothing left to decide' : `Done — ${ideasToDecide(toDecide)}`;
}

export const IDEA_COLUMN = 'Idea';
export const IDEA_TONE = 'Tone';
export const IDEA_FORMATS = 'Formats';
export const IDEA_STATUS_COLUMN = 'Status';
export const IDEA_COST = 'Cost';
export const IDEA_ACTIONS = 'Decision';
export const IDEA_KIND = { video: 'Video', image: 'Image' } as const;

/** Where one idea stands, in the badge on its row. */
export const IDEA_STATUS = {
  raw: 'To do',
  working: WORKING,
  drafted: WAITING_FOR_YOU,
  approved: APPROVED,
  declined: 'Declined',
  failed: 'Could not be worked out',
} as const;

export const APPROVE = 'Approve';
export const DECLINE = 'Decline';
export const UNDO = 'Undo';
export const OPEN_CONTENT = 'Open the content';
export const CONTENT_DELETED = 'Its content was deleted.';
export const DECLINE_TITLE = 'Decline this idea?';
export const DECLINE_REASON = 'Why not? (optional)';
export const DECLINE_REASON_HINT = 'Only for you, to remember later. You can undo a decline.';
export const DECLINE_YES = 'Decline';
export const DECLINED_BECAUSE = 'Declined:';
export const DECLINED_NO_REASON = 'Declined without a reason.';
export const APPROVE_FAILED = 'Could not approve the idea:';
export const DECLINE_FAILED = 'Could not decline the idea:';
export const UNDO_FAILED = 'Could not undo the decline:';
export const RETRY_FAILED = 'Could not try again:';
export const DELETE_FAILED = 'Could not delete the batch:';

/* --------------------------------------------------- one by one (US-124) */

export const IDEA_REVIEW_TITLE = 'Review one by one';
export const IDEA_REVIEW_BACK = 'The batch';
/** Above the first idea, until "Got it" is pressed in this browser. */
export const IDEA_REVIEW_INTRO =
  'Each card is one of your ideas as Claude worked it out: a title, a brief, the formats and a tone. Change anything that is not right, then approve it to make it a content — its planning starts at once — or decline it. Skip leaves it for later; you come back to it after the last one.';

/** "3 of 12 to decide". */
export function ideaOfToDecide(number: number, total: number): string {
  return `${String(number)} of ${String(total)} to decide`;
}

export const IDEA_SEED = 'Your idea';
export const IDEA_SUGGESTED_SEED = 'Suggested idea';
export const IDEA_RATIONALE = 'Why Claude proposes it';
export const IDEA_TITLE = 'Title';
export const IDEA_KIND_LABEL = 'Kind';
export const IDEA_FORMATS_HINT = 'Only the formats this kind can be made in.';
export const IDEA_NO_FORMATS = 'Choose at least one format.';
export const IDEA_BRIEF = 'Brief';
export const IDEA_DIRECTION = 'Creative direction';
export const OPTIONAL = 'optional';
export const IDEA_LENGTH = 'Length in seconds';
export const IDEA_LENGTH_HINT = 'Leave it empty and the planner picks a length.';
export const IDEA_TRACKS = 'Tracks';
export const IDEA_TRACKS_HINT = 'Not chosen for this idea yet means Settings → Defaults decides.';
export const IDEA_TOGGLES = {
  captions: 'Captions',
  music: 'Music',
  voiceover: 'Narrator (voice-over)',
  watermark: 'Watermark / logo bug',
} as const;
export const SKIP = 'Skip';
export const PREVIOUS = 'Previous';
export const APPROVING = 'Approving…';
export const IDEA_SAVE_FAILED = 'Could not save your changes:';
export const KEYBOARD_HINT = 'Keys: a approve · d decline · j or → skip · k or ← back · e title';
export const DISCARD_TITLE = 'Discard your changes?';
export const DISCARD_BODY = 'You changed this idea. Moving on without approving it leaves it as Claude wrote it.';
export const DISCARD_YES = 'Discard changes';
export const KEEP_EDITING = 'Keep editing';

/** The toast after an approve; its text links to the content. */
export function contentBeingPlanned(name: string): string {
  return `Content ${name} is being planned`;
}

/** "12 reviewed: 9 approved, 3 declined". */
export function ideasReviewed(approved: number, declined: number): string {
  return `${String(approved + declined)} reviewed: ${String(approved)} approved, ${String(declined)} declined`;
}

export const NOTHING_TO_REVIEW = 'Nothing in this batch is waiting for a decision.';
export const REVIEW_BACK_TO_BATCH = 'Back to the batch';

/* ----------------------------------------------------------- the new batch */

export const NEW_BATCH_SUBTITLE =
  'The ideas, and the product they are for. Claude works each one out on the next page.';
export const BATCH_TITLE = 'Title';
export const BATCH_TITLE_PLACEHOLDER = 'Autumn launch';
export const BATCH_TITLE_HINT = 'Only for you, to find the batch again.';
export const REPOSITORIES = 'Repositories';
export const BATCH_REPOSITORIES_HINT =
  'The first one picked is the primary — the product the ideas are about. The rest are read for context.';
export const BATCH_NEEDS_REPOSITORY =
  'A batch is made for a product, and no repository has a deploy key yet.';
export const ADD_ONE = 'Add one';
export const BATCH_NEEDS_REPOSITORY_END = 'first.';
export const BATCH_LANGUAGE_HINT =
  'What the ideas are written in, and later what the content speaks. Filled in from the product’s profile when it has one, otherwise from Settings → Defaults.';
export const WHERE_IDEAS_COME_FROM = 'The ideas';
export const I_HAVE_IDEAS = 'I have ideas';
export const I_HAVE_A_DOCUMENT = 'I have an ideas document';
export const SUGGEST_IDEAS = 'Suggest ideas for me';
export const IDEAS_DOCUMENT = 'The ideas document';
export const IDEAS_DOCUMENT_PLACEHOLDER =
  '# Autumn campaign\n\nRules for every script: …\n\n### 01 · Security footage\n15 s · Reel\n| s | Visual | On screen |\n…\n\n### 02 · Cold case\n…';
export const OPEN_DOCUMENT_FILE = 'Open a file';
export const DOCUMENT_FILE_FAILED = 'Could not read that file:';
/** Under the document: what is pasted, and what Claude does with it. */
export function documentHint(lines: number, characters: number, max: number): string {
  if (characters === 0) {
    return 'Paste a whole campaign file, or open one: every idea with its script, and the rules and notes around them. No limit on lines.';
  }
  const size = `${lines.toLocaleString('en')} ${lines === 1 ? 'line' : 'lines'}, ${characters.toLocaleString('en')} characters`;
  if (characters > max) {
    return `${size} — a document takes at most ${max.toLocaleString('en')} characters. Split it over two batches.`;
  }
  return `${size}. Claude finds the ideas in it first; each keeps its section word for word, and the rules and notes that hold for all of them go along with every idea.`;
}
export const BATCH_DOCUMENT = 'Your ideas document';
export const IDEAS_READING_DOCUMENT = 'Finding the ideas in your document…';
export const IDEA_SECTION = 'Its section of your document';
export const SHOW_SECTION = 'Show the section';
export const ONE_IDEA_PER_LINE = 'One idea per line';
export const IDEAS_PLACEHOLDER =
  'A reel about the new dashboard\nWhy teams switch from spreadsheets\nThree things you did not know it could do';
export const THEME = 'Theme';
export const THEME_PLACEHOLDER = 'The autumn release: faster reports and the new mobile app.';
export const THEME_HINT = 'What the ideas should be about. Claude reads the product too.';
export const HOW_MANY = 'How many ideas';

/** The hint under the list: how many there are, against the most there may be. */
export function ideaLines(count: number, max: number): string {
  return `${ideaCount(count)} of at most ${String(max)}. Empty lines and repeats are left out.`;
}

/** The hint under a pasted planning table that reads. */
export function tableRows(count: number): string {
  return `A planning table: ${ideaCount(count)}, one per row. Known columns fill in the brief details.`;
}

export const READING_TABLE = 'Reading the table…';
export const TABLE_PREVIEW = 'What the table becomes';

/** A preview row whose campaign cell named no campaign. */
export function unknownTableCampaign(name: string): string {
  return `No campaign called “${name}” — kept as a detail`;
}

/** Under the list once it is too long. */
export function tooManyIdeas(count: number, max: number): string {
  return `${ideaCount(count)} — a batch takes at most ${String(max)}. Remove some, or start a second batch.`;
}

/** Under the count: the range it may be in. */
export function howManyHint(min: number, max: number): string {
  return `Between ${String(min)} and ${String(max)}.`;
}

export const START_BATCH = 'Start working them out';
export const STARTING_BATCH = 'Starting…';
export const BATCH_STARTED = 'The batch is started.';
export const WHAT_HAPPENS_NEXT = 'What happens next';
export const BATCH_STEPS = [
  {
    title: 'Worked out',
    body: 'Claude reads the product and works each idea out into a proposal: a title, a brief, the formats and a tone. One at a time, so the list fills up as you watch.',
  },
  {
    title: 'You decide',
    body: 'Read each proposal, change what you like, and approve it or set it aside.',
  },
  {
    title: 'Content',
    body: 'An approved idea becomes a content and starts planning. Nothing is made until you mark it ready, as with any content.',
  },
] as const;

/* ------------------------------------------------------------- board (US-014) */

export const BOARD = 'Board';
export const BOARD_READ_FAILED = 'Could not read the board:';
export const BOARD_EMPTY = 'Nothing on the board yet';
export const BOARD_EMPTY_BODY = 'Make a content, or start a batch of ideas, and its card appears here.';
export const BOARD_COLUMN_EMPTY = 'Nothing here';
export const BOARD_NEW_CONTENT = 'New content';
export const BOARD_NEW_IDEAS = 'New ideas';

/** "2 need you · 3 in production · 1 posts due": the board's header line. */
export function boardSubtitle(needYou: number, inProduction: number, postsDue: number): string {
  return `${String(needYou)} need you · ${String(inProduction)} in production · ${String(postsDue)} posts due`;
}

/** "2 of 3 slots": the production slots in use, on the Production column. */
export function productionSlots(used: number, max: number): string {
  return `${String(used)} of ${String(max)} slots`;
}

/* ---------------------------------------------------- what a card shows (US-015) */

export const BOARD_THUMBNAILS = 'Thumbnails';
export const BOARD_FILTER_CAMPAIGN = 'Campaign';
export const BOARD_ALL_CAMPAIGNS = 'All';
export const BOARD_NO_CAMPAIGN = 'No campaign';
export const BOARD_ARCHIVED_CAMPAIGNS = 'Archived';
export const BOARD_FILTER_BOOK = 'Brand book';
export const BOARD_ALL_BOOKS = 'All brand books';
export const BOARD_FILTER_KIND = 'Kind';
export const BOARD_ALL_KINDS = 'Video and images';
export const BOARD_FILTER_FUNNEL = 'Funnel';
export const BOARD_ALL_FUNNEL_STAGES = 'All funnel stages';
export const BOARD_SHOW = 'Show';
export const BOARD_SEARCH = 'Search the board';
export const BOARD_SEARCH_PLACEHOLDER = 'Search titles, briefs and campaigns';
export const BOARD_ALL_POSTED = 'All posted';
export const BOARD_ARCHIVED = 'Archived';
export const BOARD_NO_MATCH = 'No cards match these filters';
export const BOARD_CLEAR_FILTERS = 'Clear filters';
export const BOARD_BLOCKED = 'Blocked';
export const BOARD_PLACED = 'Placed';
export const BOARD_POSTED = 'Posted';

/** "3/5": a card's checklist, ticked of total. */
export function checklistProgress(done: number, total: number): string {
  return `${String(done)}/${String(total)}`;
}

/** "Checklist: 3 of 5 done", the checklist badge read out. */
export function checklistTitle(done: number, total: number): string {
  return `Checklist: ${String(done)} of ${String(total)} done`;
}

/** "45 s", a video's length on a card's meta line. */
export function cardSeconds(seconds: number): string {
  return `${String(seconds)} s`;
}

/* ------------------------------------------------------------ swimlanes (US-017) */

export const BOARD_GROUP = 'Group';
export const BOARD_COLLAPSE_LANE = 'Collapse lane';
export const BOARD_EXPAND_LANE = 'Expand lane';

/** "4 of 12 posted": a campaign lane's progress. */
export function campaignPosted(posted: number, total: number): string {
  return `${String(posted)} of ${String(total)} posted`;
}

/** "16 Oct – 20 Nov", "from 16 Oct", "until 20 Nov": a campaign's days, already short. */
export function campaignRange(starts: string | null, ends: string | null): string | null {
  if (starts !== null && ends !== null) return `${starts} – ${ends}`;
  if (starts !== null) return `from ${starts}`;
  if (ends !== null) return `until ${ends}`;
  return null;
}

/* ------------------------------------------------------ the card drawer (US-018) */

export const BOARD_DRAWER = 'Card';
export const BOARD_DRAWER_CLOSE = 'Close';
export const BOARD_DRAWER_MISSING = 'This card is not on the board any more.';
export const CARD_FIELD_CAMPAIGN = 'Campaign';
export const CARD_FIELD_NO_CAMPAIGN = 'None';
export const CARD_FIELD_ARCHIVED_CAMPAIGN = '(archived)';
export const CARD_FIELD_DUE = 'Publish by';
export const CARD_FIELD_BLOCKED = 'Blocked';
export const CARD_FIELD_BLOCKED_PLACEHOLDER = 'What is it waiting on?';
export const CARD_FIELD_CLEAR = 'Clear';
export const CHECKLIST = 'Checklist';
export const CHECKLIST_EMPTY = 'No items yet.';
export const CHECKLIST_ADD = 'Add an item';
export const CHECKLIST_ADD_PLACEHOLDER = 'Add an item and press Enter';
export const CHECKLIST_DELETE = 'Delete item';
export const CHECKLIST_FULL = 'A checklist holds at most 30 items.';
export const BOARD_BRIEF_DETAILS = 'Brief details';
export const BOARD_EDIT_ON_CONTENT_PAGE = 'Edit on the content page';
export const BOARD_DUPLICATE = 'Duplicate';
export const BOARD_DUPLICATE_TITLE = 'Duplicate this content';
export const BOARD_DUPLICATE_NAME = 'Name of the copy';
export const BOARD_DUPLICATE_BODY =
  'The copy keeps the brief, settings, campaign, publish-by date and checklist (unticked), and starts in planning.';
export const BOARD_DUPLICATING = 'Duplicating…';
export const BOARD_ARCHIVE = 'Archive';
export const BOARD_UNARCHIVE = 'Unarchive';

/** "Duplicated as launch-teaser-copy". */
export function boardDuplicated(name: string): string {
  return `Duplicated as ${name}`;
}

/** "Archived launch-teaser". */
export function boardArchived(name: string): string {
  return `Archived ${name}`;
}

/** "Unarchived launch-teaser". */
export function boardUnarchived(name: string): string {
  return `Unarchived ${name}`;
}

/* ---------------------------------------- board fields on the content page (US-031) */

export const CONTENT_BOARD_PANEL = 'Board';
export const CONTENT_ARCHIVED_NOTICE = 'Archived: this content is off the board unless it shows archived cards.';
export const CONTENT_VIEW_ON_BOARD = 'View on board';

/* ------------------------------------------ autopilot (one-shot ads US-020) */

export const AUTOPILOT_BADGE = 'Autopilot';
export const AUTOPILOT_BADGE_TITLE =
  'Plans and produces without waiting for you; the narration stop is off.';
export const AUTOPILOT_TAKE_OVER = 'Take over';
export const AUTOPILOT_TAKE_OVER_TITLE =
  'Switch autopilot off for this content: from here on it stops at the usual gates.';
export const AUTOPILOT_HAND_BACK = 'Hand back';
export const AUTOPILOT_HAND_BACK_TITLE =
  'Switch autopilot on again: this content plans and produces without waiting for you.';

/* ---------------------------------------------------- the idea drawer (US-020) */

export const BOARD_YOUR_LINE = 'Your line';
export const BOARD_IDEA_BATCH = 'Batch';
export const BOARD_IDEA_LENGTH = 'Length';
/** An idea without a length: the planner decides it. */
export const BOARD_IDEA_LENGTH_OPEN = 'The planner picks';
export const BOARD_EDIT_IN_REVIEW = 'Edit in review';

/** "Declined “Launch teaser”". */
export function boardIdeaDeclined(title: string): string {
  return `Declined “${title}”`;
}

/* ------------------------------- the drawer in planning and production (US-021) */

export const BOARD_STAGE = 'Where it stands';
export const BOARD_OPEN_PLANNING_TERMINAL = 'Open the planning terminal';
export const BOARD_LEAVE_QUEUE = 'Leave queue';
export const BOARD_STOP_PRODUCTION = 'Stop production';
export const BOARD_HOLD_LIFTS_UNKNOWN = 'It resumes on its own as soon as the limit lifts.';
export const BOARD_FAILED_STAGE = 'Failed at';
export const BOARD_FAILED_ERROR = 'Error';
export const BOARD_RETRY_RAISE_CAP = 'Raise the spend cap on the content page; that retries it.';

/** "The plan has 3 problems to fix." */
export function boardPlanProblems(count: number): string {
  return `The plan has ${String(count)} ${count === 1 ? 'problem' : 'problems'} to fix.`;
}

/** "8 steps in the plan." */
export function boardStepCount(count: number): string {
  return `${String(count)} ${count === 1 ? 'step' : 'steps'} in the plan.`;
}

/** "Part 3 of 8" — the current step's title follows on its own line. */
export function boardPartOf(position: number, total: number): string {
  return `Part ${String(position)} of ${String(total)}`;
}

/** "It resumes on its own in 12:04, at Thu 16 Oct 09:00." */
export function boardHoldLifts(countdown: string, at: string): string {
  return `It resumes on its own in ${countdown}, at ${at}.`;
}

/** Which approval gate a content is stopped at. */
export function boardGate(gate: 'sketch' | 'script'): string {
  return gate === 'sketch' ? 'Gate: the scenes, before the video is made' : 'Gate: the narration, before it is spoken';
}

/** "Retry will: …". */
export function boardRetryPlan(label: string, summary: string): string {
  return `${label} will: ${summary}`;
}

/** "launch-teaser is back in planning." */
export function boardBackToPlanning(name: string): string {
  return `${name} is back in planning. The steps stay until the plan is marked ready again.`;
}

/** "launch-teaser left the production queue." */
export function boardLeftQueue(name: string): string {
  return `${name} left the production queue. Start it again whenever you like.`;
}

/** "Stopped launch-teaser." */
export function boardStopped(name: string): string {
  return `Stopped. ${name} is ready again and resumes at the first step that is not done.`;
}

/* --------------------------------------- the planning conversation (US-023) */

export const PLANNING_CONVERSATION = 'Planning with Claude';
export const PLANNING_THINKING = 'Claude is thinking';
export const PLANNING_HERE = 'Plan with Claude here';
export const PLANNING_HERE_HINT =
  'Claude reads the project files and the brief, then writes its questions here. Answer them below; nothing is produced until you mark the content ready.';
export const PLANNING_NO_CONTAINER = 'Planning can start once the project files are fetched.';
export const PLANNING_REPLY = 'Your reply';
export const PLANNING_REPLY_HINT = 'Ctrl+Enter (⌘+Enter on a Mac) sends it.';
export const PLANNING_SEND = 'Send';
export const PLANNING_TAKE_ALL = 'Take every recommendation';
export const PLANNING_STOP = 'Stop';
export const PLANNING_NO_REPLY = 'The planner has not answered yet.';
export const PLANNING_PLAN_PARSES = 'The plan is written and reads cleanly.';
export const PLANNING_TERMINAL_OPEN = 'The planning terminal is open, and two planners would overwrite each other’s plan.';
export const PLANNING_TERMINAL_LINK = 'Go to the terminal on the content page';
export const PLANNING_STOPPED = 'Stopped. The conversation is kept: send a reply to continue it.';

/** "Exchange 3 · $0.42 (total $1.10)". */
export function planningExchange(exchange: number, costUsd: number | null, totalCostUsd: number): string {
  const cost = costUsd === null ? '' : ` · ${dollars(costUsd)}`;
  const total = exchange > 1 && totalCostUsd > 0 ? ` (total ${dollars(totalCostUsd)})` : '';
  return `Exchange ${String(exchange)}${cost}${total}`;
}

/* ------------------------------------------ the primary action of a card (US-019) */

export const ACTION_APPROVE_IDEA = 'Approve';
export const ACTION_TRY_AGAIN = 'Try again';
export const ACTION_FETCH_AGAIN = 'Fetch the project files again';
export const ACTION_MARK_READY = 'Mark ready';
export const ACTION_ANSWER_CLAUDE = 'Answer Claude';
/** An autopilot planner answered with its own decisions (US-021): nothing to answer, only to read. */
export const ACTION_SEE_DECISIONS = 'See Claude’s decisions';
export const ACTION_PLAN_WITH_CLAUDE = 'Plan with Claude';
export const ACTION_START_PRODUCTION = 'Start production';
export const ACTION_RESUME_NOW = 'Resume now';
export const ACTION_OPEN_TO_APPROVE = 'Open to approve';
export const ACTION_RETRY = 'Retry';
export const ACTION_RAISE_CAP = 'Raise the cap';
export const ACTION_SCHEDULE = 'Schedule';
export const ACTION_MARK_POSTED = 'Mark posted';
export const ACTION_RUNNING = 'Working…';
export const ACTION_RETRY_REFUSED = 'This content cannot be retried.';
export const ACTION_QUEUED_FOR_HOLD = 'Queued until Claude’s limit lifts';

/** "Approve version 2". */
export function actionApproveVersion(version: number): string {
  return `Approve version ${String(version)}`;
}

/** "Approved version 2 of launch-teaser". */
export function actionVersionApproved(name: string, version: number): string {
  return `Approved version ${String(version)} of ${name}`;
}

/** After **Fetch the project files again**. */
export function actionSetUp(name: string): string {
  return `${name} is cloned and ready to plan.`;
}

/** After a **Retry** that runs the setup again. */
export function actionSetUpAgain(name: string): string {
  return `${name} is being set up again.`;
}

/** After a **Retry** that resumes production. */
export function actionResumes(name: string, stepId: string | null): string {
  return `${name} resumes at ${stepId ?? 'its first outstanding step'}.`;
}

/** After **Plan with Claude**. */
export function actionPlanningStarted(name: string): string {
  return `Claude is planning ${name}`;
}

/** **Start production** with every slot taken. */
export function actionQueued(name: string, position: number): string {
  return `${name} is #${String(position)} in the production queue`;
}

/** **Start production** with a free slot. */
export function actionProducing(name: string): string {
  return `Producing ${name}`;
}

export const APPROVE_IDEA_TITLE = 'Approve this idea?';

/** ApproveIdeaDialog's question (§ Dialogs). */
export function approveIdeaBody(title: string): string {
  return `Approve “${title}”? It becomes a content and planning starts on its own. This cannot be undone.`;
}

export const MARK_READY_ESTIMATE = 'Estimated cost';
export const MARK_READY_CAP = 'Spend cap';
export const MARK_READY_NO_CAP = 'None';
export const MARK_READY_READING = 'Reading the plan…';
export const MARK_READY_START = 'Start production straight away';
export const MARK_READY_BUSY = 'Marking ready…';

/** "Mark launch-teaser ready?". */
export function markReadyTitle(name: string): string {
  return `Mark ${name} ready?`;
}

/** The toast after **Mark ready** without starting. */
export function markedReady(name: string): string {
  return `${name} is ready`;
}

/** "The plan does not parse yet: 2 problems to fix." */
export function markReadyProblems(count: number): string {
  return `The plan does not parse yet: ${String(count)} ${count === 1 ? 'problem' : 'problems'} to fix.`;
}

export const SCHEDULE_TITLE = 'Schedule a post';
export const SCHEDULE_CONFIRM = 'Schedule';
export const SCHEDULE_BUSY = 'Scheduling…';
export const POST_PLATFORM = 'Platform';
export const POST_PAID = 'Paid ad';
export const POST_LABEL = 'Label';
export const POST_LABEL_HINT = 'Where it goes; required for Other.';
export const POST_FORMAT = 'Format';
export const POST_NO_FORMAT = 'None';
export const POST_WHEN = 'Date and time';
export const POST_NOTES = 'Notes';
export const POST_URL = 'Link to the post';

/** "Scheduled launch-teaser on LinkedIn". */
export function scheduled(name: string, platform: string): string {
  return `Scheduled ${name} on ${platform}`;
}

export const MARK_POSTED_TITLE = 'Mark it posted';
export const MARK_POSTED_CONFIRM = 'Mark posted';
export const MARK_POSTED_BUSY = 'Marking posted…';
export const MARK_POSTED_AT = 'Posted at';
export const MARK_POSTED_JUST_PLACED = 'Just mark it placed';
export const MARK_POSTED_NONE = 'No post is planned yet. Say where it went out:';
export const MARK_POSTED_PICK = 'Which posts went out?';

/** "Posted launch-teaser" after MarkPostedDialog. */
export function markedPosted(name: string): string {
  return `Posted ${name}`;
}

/** "Marked launch-teaser placed". */
export function markedPlaced(name: string): string {
  return `Marked ${name} placed`;
}

export const RESUME_HOLD_TITLE = 'Resume every held content?';
export const RESUME_HOLD_CONFIRM = 'Resume all';
export const RESUME_HOLD_BUSY = 'Resuming…';

/** After **Resume now**. */
export function holdLifted(resumed: number): string {
  return resumed === 0
    ? 'The hold is lifted. Nothing could start yet; every held content is on the queue.'
    : `The hold is lifted and ${String(resumed)} ${resumed === 1 ? 'content is' : 'contents are'} producing again.`;
}

/* --------------------------- the drawer in review and after (US-022) */

export const BOARD_VERSION = 'The version';
export const BOARD_FULL_REVIEW = 'Open the full review';
export const BOARD_NO_VERSION = 'There is no rendered version to show yet.';
export const BOARD_VERSION_LOADING = 'Reading the versions…';
export const BOARD_APPROVE_WAITS = 'Claude is still checking this version; approve it once the round is done.';

/** "Claude is checking it, round 2". */
export function boardCheckingRound(round: number): string {
  return `Claude is checking it, round ${String(round)}`;
}

/** The label over the request: "What should change in version 2 of launch-teaser?" */
export function boardRequestChangesFor(name: string, version: number): string {
  return `What should change in version ${String(version)} of ${name}?`;
}

/** Under the request. */
export function boardChangesHint(version: number): string {
  return `Claude amends the plan with your request and makes version ${String(version + 1)}. Version ${String(version)} stays as it is. Ctrl+Enter (⌘+Enter) sends it.`;
}

/** After **Request changes**, when the server gives no message of its own. */
export function boardChangesRequested(name: string, version: number): string {
  return `Making version ${String(version)} of ${name}.`;
}

/* ------------------------------------------------- the posts panel (US-024) */

export const POSTS = 'Posts';
export const POSTS_LOADING = 'Reading the posts…';
export const POSTS_NONE = 'No post is planned yet.';
export const POST_CAPTION = 'Caption';
export const POST_CAPTION_HINT = 'Left empty, Copy caption takes the share copy for this platform.';
export const POST_AD = 'Ad';
export const POST_NO_DATE = 'No date';
export const POST_DUE = 'Due';
export const POST_COPY_CAPTION = 'Copy caption';
export const POST_NO_SHARE_COPY = 'No share copy yet';
export const POST_NOT_IN_BUCKET = 'Not in the bucket yet';
export const POST_NO_RENDER = 'No rendered version yet';
export const POST_MARK_POSTED = 'Mark posted';
export const POST_APPROVE_FIRST = 'Approve a version first';
export const POST_EDIT = 'Edit';
export const POST_DELETE = 'Delete';
export const POST_OPEN = 'Open post';
export const POST_EDIT_NOTES = 'Edit notes';
export const POST_ADD = 'Add post';
export const POST_ADD_BUSY = 'Adding…';
export const POST_PLAN_PER_PLATFORM = 'Plan a post per platform in the share copy';
export const POST_PLAN_PER_PLATFORM_NONE = 'Every platform in the share copy has a post.';
export const POST_DELETE_TITLE = 'Delete this post?';
export const POST_SAVING = 'Saving…';

/** "Posted Thu 16 Oct, 09:12". */
export function postedOn(moment: string): string {
  return `Posted ${moment}`;
}

/** "Open LinkedIn", "Open Ads Manager". */
export function openComposer(name: string): string {
  return `Open ${name}`;
}

/** The delete confirmation: "The LinkedIn post of launch-teaser goes, with its notes." */
export function postDeleteBody(platform: string, name: string): string {
  return `The ${platform} post of ${name} goes, with its caption and notes. This cannot be undone.`;
}

/** "Planned 2 posts for launch-teaser". */
export function postsPlanned(count: number, name: string): string {
  return `Planned ${String(count)} ${count === 1 ? 'post' : 'posts'} for ${name}`;
}

/** "Posted launch-teaser on LinkedIn". */
export function postedOnPlatform(name: string, platform: string): string {
  return `Posted ${name} on ${platform}`;
}

/** "launch-teaser on LinkedIn is planned again". */
export function postUnposted(name: string, platform: string): string {
  return `${name} on ${platform} is planned again`;
}

/** "Deleted the LinkedIn post". */
export function postDeleted(platform: string): string {
  return `Deleted the ${platform} post`;
}

/* ------------------------------------------------ moving a card (US-025) */

// Why a drop is refused, Design reference § Moving a card.
export const MOVE_IDEA_NOT_WORKED_OUT = "Claude hasn't worked this idea out yet.";
export const MOVE_IDEA_FAILED = 'This idea could not be worked out. Try again first.';
export const MOVE_IDEA_TO_PLANNING_FIRST = 'An idea goes to Planning first.';
export const MOVE_NO_FINISHED_PLAN = 'There is no finished plan yet. Plan it first.';
export const MOVE_STOP_PRODUCTION_FIRST = 'Stop production first.';
export const MOVE_UNDO_POSTING_FIRST = 'Undo posting first: drop it on To post.';
export const MOVE_APPROVE_FIRST = 'Approve it first: drop it on To post.';
export const MOVE_PRODUCTION_HAS_TO_FINISH = 'Production has to finish first.';
export const MOVE_NOT_AN_IDEA_AGAIN = 'A content cannot become an idea again.';
export const MOVE_REVIEW_AFTER_PRODUCTION = 'A version reaches Review when production finishes.';
/** Not in the table: a made version cannot go back to Planning. */
export const MOVE_BACK_TO_PLANNING = 'A made version does not go back to Planning. Drop it on Production to ask for changes.';
export const MOVE_LANE_BOOK = 'Change the brand book on the content page.';
export const MOVE_LANE_FUNNEL = 'Change the funnel stage in the brief details.';
export const MOVE_LANE_ARCHIVED = 'That campaign is archived. Unarchive it first.';

/** The polite announcement after a move: "Moved “launch-teaser” to To post". */
export function boardMoved(title: string, place: string): string {
  return `Moved “${title}” to ${place}`;
}

// The card menu, US-026.
export const CARD_MENU = 'Card actions';
export const CARD_MENU_OPEN = 'Open';
export const CARD_MENU_MOVE_TO = 'Move to…';
export const CARD_MENU_HERE = 'It is in this column.';
export const CARD_MENU_MOVE_UP = 'Move up';
export const CARD_MENU_MOVE_DOWN = 'Move down';
export const CARD_MENU_TO_TOP = 'To top';
export const CARD_MENU_TO_BOTTOM = 'To bottom';
export const CARD_MENU_AT_TOP = 'Already at the top.';
export const CARD_MENU_AT_BOTTOM = 'Already at the bottom.';
export const REORDER_FAILED = 'Could not reorder:';

/** The polite announcement after a reorder: "“launch-teaser” is now 2 of 5 in Planning". */
export function boardReordered(title: string, position: number, count: number, column: string): string {
  return `“${title}” is now ${String(position)} of ${String(count)} in ${column}`;
}

export const UNSCHEDULE_TITLE = 'Clear the dates?';
export const UNSCHEDULE_CONFIRM = 'Clear the dates';
export const UNSCHEDULE_BUSY = 'Clearing…';

/** "Clear the date of 2 planned posts? They stay planned, without a date." */
export function unscheduleBody(count: number): string {
  return `Clear the date of ${String(count)} planned ${count === 1 ? 'post' : 'posts'}? They stay planned, without a date.`;
}

/** "The posts of launch-teaser have no date now". */
export function unscheduled(name: string): string {
  return `The posts of ${name} have no date now`;
}

export const UNDO_POSTING_TITLE = 'Undo posting?';
export const UNDO_POSTING_CONFIRM = 'Undo posting';
export const UNDO_POSTING_BUSY = 'Undoing…';

/** "Undo posting? 2 posts go back to planned and the content is no longer placed." */
export function undoPostingBody(count: number): string {
  return `Undo posting? ${String(count)} ${count === 1 ? 'post goes' : 'posts go'} back to planned and the content is no longer placed.`;
}

/** Undo posting on a content placed without posts. */
export const UNDO_POSTING_PLACED_BODY = 'Undo posting? The content is no longer placed.';

/** "launch-teaser is no longer posted". */
export function postingUndone(name: string): string {
  return `${name} is no longer posted`;
}

// ---------------------------------------------------------------- board keys (US-027)

export const BOARD_SHORTCUTS = 'Keyboard shortcuts';
export const BOARD_SHORTCUTS_CLOSE = 'Close';
export const BOARD_SHORTCUTS_ALL = 'All keys';
export const BOARD_NOTHING_NEEDS_YOU = 'Nothing needs you';

// ---------------------------------------------------------------- bulk actions (US-028)

/** "3 selected". */
export function bulkSelected(count: number): string {
  return `${String(count)} selected`;
}

export const BULK_BAR = 'Selected cards';
export const BULK_SET_CAMPAIGN = 'Set campaign';
export const BULK_SET_DUE = 'Set publish by';
export const BULK_BLOCK = 'Block…';
export const BULK_UNBLOCK = 'Unblock';
export const BULK_APPROVE_IDEAS = 'Approve ideas';
export const BULK_DECLINE_IDEAS = 'Decline ideas';
export const BULK_START_PRODUCTION = 'Start production';
export const BULK_MARK_POSTED = 'Mark posted';
export const BULK_ARCHIVE = 'Archive';
export const BULK_REJECT = 'Reject…';
export const BULK_UNREJECT = 'Un-reject';
export const BULK_CLEAR = 'Clear selection';
export const BULK_NONE_QUALIFIES = 'None of the selected cards qualifies.';
export const BULK_WORKING = 'Working…';
export const BULK_APPLY = 'Apply';

export const BULK_CAMPAIGN_TITLE = 'Set the campaign';
export const BULK_CAMPAIGN_FIELD = 'Campaign';
export const BULK_DUE_TITLE = 'Set the publish-by date';
export const BULK_DUE_FIELD = 'Publish by';
export const BULK_DUE_HINT = 'Leave it empty to clear the date.';
export const BULK_BLOCK_TITLE = 'Block the cards';
export const BULK_BLOCK_FIELD = 'Reason';
export const BULK_BLOCK_HINT = 'One reason for every selected card.';
export const BULK_REJECT_TITLE = 'Reject the contents';
export const BULK_REJECT_HINT = 'One reason for every selected content. Leave it empty to give none.';

/** "For 3 selected cards." */
export function bulkDialogBody(count: number): string {
  return `For ${String(count)} selected ${count === 1 ? 'card' : 'cards'}.`;
}

export const BULK_SKIP_NOT_DRAFTED = 'not an idea waiting for a decision.';
export const BULK_SKIP_NOT_BLOCKED = 'not blocked.';
export const BULK_SKIP_NOT_READY = 'not a content that is ready for production.';
export const BULK_SKIP_QUEUED = 'already in the production queue.';
export const BULK_SKIP_NOT_DELIVERED = 'not a delivered content.';
export const BULK_SKIP_PLACED = 'already posted.';
export const BULK_SKIP_REJECTED = 'rejected; it cannot be posted.';
export const BULK_SKIP_NOT_REJECTED = 'not a rejected content.';
export const BULK_SKIP_IDEA = 'an idea; only contents are archived.';
export const BULK_SKIP_RUNNING = 'running; stop it first.';
export const BULK_SKIP_ARCHIVED = 'already archived.';
export const BULK_SKIP_BUSY = 'busy with another action.';

/** "“launch-teaser”: not blocked." */
export function bulkReason(title: string, reason: string): string {
  return `“${title}”: ${reason}`;
}

/** "3 done, 1 skipped." */
export function bulkDone(done: number, skipped: number): string {
  return `${String(done)} done, ${String(skipped)} skipped.`;
}

/** "4 more ideas were left selected: Approve ideas takes 10 at a time." */
export function bulkLeftSelected(count: number, max: number): string {
  return `${String(count)} more ${count === 1 ? 'idea was' : 'ideas were'} left selected: Approve ideas takes ${String(max)} at a time.`;
}

/** "3 rejected, 1 skipped.", the count of a bulk reject. */
export function bulkRejected(done: number, skipped: number): string {
  return `${String(done)} rejected, ${String(skipped)} skipped.`;
}

/** After a bulk reject that rejected some: the pauses run afterwards, and a failure shows on its content page. */
export function bulkRejectedAds(count: number): string {
  return `${count === 1 ? 'Its' : 'Their'} Meta ads are being paused in the background.`;
}

// ---------------------------------------------------------------- quick add (US-029)
export const QUICK_ADD = 'Add ideas';
export const QUICK_ADD_OPEN = 'Add ideas (n)';
export const QUICK_ADD_PLACEHOLDER = 'One idea per line, or paste a planning table';
export const QUICK_ADD_HINT = 'Enter adds one idea; Ctrl/Cmd+Enter adds several. Esc closes.';
export const QUICK_ADD_REPOSITORY = 'Primary repository';
export const QUICK_ADD_CAMPAIGN = 'Campaign';
export const QUICK_ADD_SUBMIT = 'Add';
export const QUICK_ADD_ADDING = 'Adding…';
export const QUICK_ADD_NEEDS_REPOSITORY =
  'Ideas are made for a product, and no repository has a deploy key yet. Add a deploy key to one first:';
export const QUICK_ADD_TO_REPOSITORIES = 'Repositories';

export function quickAdded(count: number): string {
  return count === 1 ? 'Idea added. It is worked out next.' : `${String(count)} ideas added. They are worked out next.`;
}

// ------------------------------------------------------- campaigns (US-030)
export const CAMPAIGNS = 'Campaigns';
export const CAMPAIGNS_EMPTY = 'No campaigns yet. A campaign groups ideas and contents into one lane on the board.';
export const CAMPAIGN_NEW = 'New campaign';
export const CAMPAIGN_EDIT = 'Edit campaign';
export const CAMPAIGN_BACK = 'All campaigns';
export const CAMPAIGN_ARCHIVED = 'Archived';
export const CAMPAIGN_NAME = 'Name';
export const CAMPAIGN_COLOR = 'Colour';
export const CAMPAIGN_BRAND_BOOK = 'Brand book';
export const CAMPAIGN_NO_BRAND_BOOK = 'None';
export const CAMPAIGN_STARTS_ON = 'Starts on';
export const CAMPAIGN_ENDS_ON = 'Ends on';
export const CAMPAIGN_GOAL = 'Goal';
export const CAMPAIGN_NOTES = 'Notes';
export const CAMPAIGN_CHECKLIST = 'Checklist template';
export const CAMPAIGN_CHECKLIST_HINT =
  'One item per line, at most 20. A content that joins the campaign gets these items, unticked.';
export const CAMPAIGN_PLATES = 'Plates';
export const CAMPAIGN_PLATES_HINT =
  'One approved still per character and per location, by its library item id. Every ad in the campaign makes its pictures of that person or place from it. The first still a content makes for one without a plate is set here automatically.';
export const CAMPAIGN_PLATES_ADD = 'Add a plate';
export const CAMPAIGN_PLATE_INCOMPLETE = 'Every plate needs a name and a library item id.';
export const CAMPAIGN_SAVE = 'Save';
export const CAMPAIGN_SAVING = 'Saving…';
export const CAMPAIGN_ARCHIVE = 'Archive';
export const CAMPAIGN_UNARCHIVE = 'Unarchive';
export const CAMPAIGN_DELETE = 'Delete';
export const CAMPAIGN_DELETING = 'Deleting…';
export const CAMPAIGN_DELETE_TITLE = 'Delete the campaign';
export const CAMPAIGN_CLOSE = 'Close';
export const CAMPAIGN_ENDS_BEFORE_START = 'The campaign cannot end before it starts.';
export const CAMPAIGN_FIELD_HINT = 'Optional. New work starts in this campaign’s lane.';
export const NEW_CONTENT_DUE = 'Publish by';
export const NEW_CONTENT_DUE_HINT = 'Optional. The board warns as the date comes near.';

/** The colour names the swatches are read out as. */
export const CAMPAIGN_COLOR_NAME: Record<CampaignColor, string> = {
  blue: 'Blue',
  teal: 'Teal',
  green: 'Green',
  amber: 'Amber',
  red: 'Red',
  purple: 'Purple',
  gray: 'Gray',
};

/** "3 ideas · 5 contents · 2 posted": what a campaign holds. */
export function campaignCounts(counts: { ideas: number; contents: number; posted: number }): string {
  const plural = (n: number, one: string, many: string): string => `${String(n)} ${n === 1 ? one : many}`;
  return `${plural(counts.ideas, 'idea', 'ideas')} · ${plural(counts.contents, 'content', 'contents')} · ${String(counts.posted)} posted`;
}

/** The checklist template is too long, by item count or by one item's length. */
export function campaignChecklistTooLong(items: number, max: number): string {
  return `${String(items)} items: a template holds at most ${String(max)}.`;
}
export function campaignChecklistItemTooLong(max: number): string {
  return `Every item is at most ${String(max)} characters.`;
}

/** The ConfirmDialog before a campaign is deleted. */
export function campaignDeleteBody(name: string, cards: number): string {
  return `Delete “${name}”? Its ${String(cards)} cards keep everything else and lose the campaign.`;
}

export function campaignSaved(name: string): string {
  return `Saved “${name}”.`;
}
export function campaignArchived(name: string, archived: boolean): string {
  return archived ? `Archived “${name}”.` : `Unarchived “${name}”.`;
}
export function campaignDeleted(name: string): string {
  return `Deleted “${name}”.`;
}

/* ------------------------------------------------------- the calendar (US-032) */

export const BOARD_VIEW = 'View';
export const BOARD_VIEW_BOARD = 'Board';
export const BOARD_VIEW_CALENDAR = 'Calendar';
export const CALENDAR_PERIOD = 'Period';
export const CALENDAR_PREVIOUS = 'Previous';
export const CALENDAR_NEXT = 'Next';
export const CALENDAR_TODAY = 'Today';
export const CALENDAR_READ_FAILED = 'Could not read the calendar:';
export const CALENDAR_AD = 'Ad';
export const CALENDAR_PAID = 'Paid post';

/** "Due · Launch video": a publish-by date on the calendar. */
export function calendarDue(title: string): string {
  return `Due · ${title}`;
}

/** "+3 more": the entries a month cell leaves out until it is opened. */
export function calendarMore(count: number): string {
  return `+${String(count)} more`;
}

/* ---------------------------------------- scheduling by dragging (US-033) */

export const CALENDAR_TO_POST = 'To post';
export const CALENDAR_TO_POST_HINT = 'Drag one onto a day to plan a post.';
export const CALENDAR_TO_POST_EMPTY = 'Nothing waits to be posted.';

/** "Moved “Launch video” to Thu 16 Oct, 09:00.": a post dragged to another day. */
export function calendarPostMoved(name: string, when: string): string {
  return `Moved “${name}” to ${when}.`;
}

/** "“Launch video” is now due 16 Oct.": a publish-by date dragged to another day. */
export function calendarDueMoved(title: string, day: string): string {
  return `“${title}” is now due ${day}.`;
}

/* -------------------------------------------- notifications (US-034) */

export const NOTIFY_ON = 'Notifications are on: you hear when something starts to need you. Click to switch off.';
export const NOTIFY_OFF = 'Notify me when something starts to need you while this tab is hidden';
export const NOTIFY_BLOCKED = 'Notifications are blocked in this browser';
export const NOTIFY_UNSUPPORTED = 'This browser cannot show notifications';
export const NOTIFY_LABEL = 'Notifications';

/* ------------------------------------- rejected contents (rejected-status) */

export const CONTENT_REJECT = 'Reject';
export const CONTENT_UNREJECT = 'Un-reject';
export const CONTENT_REJECTING = 'Rejecting…';
export const CONTENT_UNREJECTING = 'Un-rejecting…';

/** "Reject launch-teaser?" */
export function rejectTitle(name: string): string {
  return `Reject ${name}?`;
}

export const REJECT_BODY = "Rejecting stops this content from being used: its Meta ads are paused and it can't be posted.";
export const REJECT_REASON_LABEL = 'Reason (optional)';
export const REJECT_REASON_PLACEHOLDER = 'Off-brand, factually wrong, or simply not good enough…';

/** "Un-reject launch-teaser?" */
export function unrejectTitle(name: string): string {
  return `Un-reject ${name}?`;
}

export const UNREJECT_BODY =
  'It becomes delivered again: it can be posted and the ads agent can use it. Its paused Meta ads are not resumed automatically; resume them yourself if you want them back.';

/** "Rejected launch-teaser." */
export function contentRejected(name: string): string {
  return `Rejected ${name}.`;
}

/** "launch-teaser is delivered again." */
export function contentUnrejected(name: string): string {
  return `${name} is delivered again.`;
}

export const REJECTED_SENTENCE = 'Rejected. This content is not used: no ads, no posts.';

/** "Rejected 24 Oct 2026, 14:02." */
export function rejectedOn(when: string): string {
  return `Rejected ${when}.`;
}

export const REJECTION_REASON = 'Reason';
export const AD_PAUSE_PAUSING = "Pausing this content's Meta ads…";
export const AD_PAUSE_DONE = 'Its Meta ads are paused.';
export const AD_PAUSE_FAILED = 'These Meta ads could not be paused and are still running:';
export const AD_PAUSE_RETRY = 'Try pausing again';
export const AD_PAUSE_RETRYING = 'Pausing again…';
export const POSTS_REJECTED = 'This content is rejected, so it cannot be posted. Un-reject it to post it.';
